import Modal from "../../General/ConfirmableModal";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Button, IconButton, Typography } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import {
  moneyRequestFieldConfig,
  moneyRequestFieldInitialValues,
} from "../../../formConfigs/Fleet/moneyRequestFieldConfig";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import { useMsal } from "@azure/msal-react";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import TruckPopup from "../TruckPopup/TruckPopup";
import DriverPopup from "../DriverPopup/DriverPopup";
import { fetchAllDrivers } from "../IncidentPopup/IncidentPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import { RelayCodeRequest } from "../CreateRelayCodePopup/CreateRelayCodePopup";
import { ContentCopy } from "@mui/icons-material";

const MoneyRequestPopup = () => {
  const [open, setOpen] = useState(false);
  const [driverData, setDriverData] = useState<OptionType[]>([]);
  const [truckData, setTruckData] = useState<OptionType[]>([]);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formValues, setFormValues] = useState(moneyRequestFieldInitialValues);
  const [response, setResponse] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const { accounts } = useMsal();
  const prevAssetNumber = useRef();
  const prevDriverId = useRef();

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: !!token,
  });

  useEffect(() => {
    if (allDrivers) {
      setDriverData(allDrivers);
    }
  }, [allDrivers]);

  const moneyRequestMutation = useMutation({
    mutationFn: (data: any) =>
      axios
        .post(`${apiVariable}/createMoneyRequest`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => response.data),
    onSuccess: () => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["moneyRequests"] });
      queryClient.invalidateQueries({ queryKey: ["needingApprovalRequests"] });
      queryClient.invalidateQueries({ queryKey: ["allRequests"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit money request");
    },
  });

  const createRelayCode = async (data: RelayCodeRequest, company: string) => {
    const result = await axios.post(`${apiVariable}/relayCode`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { company: company },
    });

    setResponse(result.data);
    setModalOpen(true);
    queryClient.invalidateQueries({ queryKey: ["relayCodes", company] });
  };

  const handleCopyCode = () => {
    if (response?.code) {
      navigator.clipboard.writeText(response.code);
      alert("Code copied to clipboard!");
    }
  };

  const submitHandler = (data: any) => {
    const requiredFields = [
      "driverId",
      "truckId",
      "amount",
      "moneyRequestType",
      "description",
      "requiresApproval",
      "nameRequestedBy",
    ];

    const missingFields = requiredFields.filter(
      (field) => !data[field] && data[field] !== false
    );

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all fields.");
      return;
    }

    if (accounts.length > 0) {
      data.usernameRequestedBy = accounts[0].username;
    } else {
      setStatus("error");
      setErrorMessage("User information is not available.");
      return;
    }

    if (data.moneyRequestType === "EscortFee" && data.loadNumber) {
      data.description = `${data.description} load number: ${data.loadNumber}`;
      delete data.loadNumber;
    }

    data.driverId = getId(data.driverId);
    data.truckId = getId(data.truckId);

    const paymentMethod = data.paymentMethod?.toLowerCase?.() ?? "";
    if (paymentMethod === "efs") {
      data.referenceId = data.referenceId?.trim();
      if (!data.referenceId) {
        data.referenceId = null;
      }
    } else {
      data.referenceId = null;
    }

    if (data.paymentMethod === "RelayCode") {
      const selectedDriver = driverData.find((d) => d.id === data.driverId);
      const selectedTruck = truckData.find((t) => t.id === data.truckId);

      const driverFullName = selectedDriver
        ? selectedDriver.label.split("|")[0].trim()
        : "Unknown Driver";
      const unitNumber = selectedTruck
        ? selectedTruck.label.split("|")[0].trim()
        : "Unknown Unit";

      const note = `Lumper fee for ${driverFullName} with unit number ${unitNumber}`;

      createRelayCode(
        {
          constraint_amount: Number(parseFloat(data.amount).toFixed(2)) * 100,
          note: note,
        },
        data.issuedFor
      );
    }

    moneyRequestMutation.mutate(data);
  };

  const handleFetchAssignedDriver = async (truckId: string) => {
    try {
      const response = await axios.get(
        `${apiVariable}/getAssignedDriverOnTruck/${truckId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const assignedDriver = response.data;

      if (assignedDriver && assignedDriver.id) {
        const formattedAssignedDriver = {
          label: `${assignedDriver.firstName} ${assignedDriver.lastName} | ${assignedDriver.phoneNumber}`,
          id: assignedDriver.id.toString(),
        };
        setDriverData([formattedAssignedDriver]);
      } else {
        setDriverData(allDrivers || []);
      }
    } catch (error) {
      setErrorMessage(
        "No driver is assigned to this truck. Please select a driver manually."
      );
      setStatus("error");
      setDriverData(allDrivers || []);
    }
  };

  const onValuesChange = (values: any) => {
    const assetNumber = getId(values.truckId);
    const driverId = getId(values.driverId);

    const assetNumberChanged = prevAssetNumber.current !== assetNumber;
    const driverIdChanged = prevDriverId.current !== driverId;
    setFormValues((prevValues) => ({ ...prevValues, ...values }));

    if (assetNumberChanged) {
      if (assetNumber) {
        setDriverData([]);
        handleFetchAssignedDriver(assetNumber);
      } else {
        setDriverData(allDrivers || []);
      }
    } else {
      setDriverData(allDrivers || []);
    }

    if (driverIdChanged && prevDriverId.current && !driverId) {
      setDriverData(allDrivers || []);
    }

    prevAssetNumber.current = assetNumber;
    prevDriverId.current = driverId;
  };

  const formFields = moneyRequestFieldConfig.filter((field) => {
    if (field.name === "loadNumber") {
      return formValues.moneyRequestType === "EscortFee";
    }
     if (field.name === "referenceId") {
      return formValues.paymentMethod?.toLowerCase?.() === "efs";
    }
    return true;
  });

  useQuery({
    queryKey: ["trucksTable"],
    queryFn: async () => {
      const res = await axios.get(`${apiVariable}/getAllTrucks`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { areActive: true },
      });
      const formattedData = res.data.map((truck: any) => ({
        label: `${truck.unitNumber} | ${truck.make} ${truck.model} ${truck.year}`,
        id: truck.id,
      }));
      setTruckData(formattedData);
      return res.data;
    },
    enabled: open,
  });

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Create Money Request
      </Button>
      <GenericFormPopup
        formFields={formFields}
        initialValues={moneyRequestFieldInitialValues}
        title="Money Request Form"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        autocompleteData={{ drivers: driverData, trucksTable: truckData }}
        loading={moneyRequestMutation.isPending}
        includeApproval={true}
        onValuesChange={onValuesChange}
        fieldComponents={{
          truckId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <TruckPopup />
            </Box>
          ),
          driverId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <DriverPopup />
            </Box>
          ),
        }}
      />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6">Relay Code Created Successfully</Typography>
          {response?.code && (
            <Typography
              variant="body1"
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ marginRight: 8 }}>{response.code}</span>
              <IconButton onClick={handleCopyCode}>
                <ContentCopy />
              </IconButton>
            </Typography>
          )}
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setModalOpen(false)}
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </Box>
      </Modal>
      <StatusSnackbar
        status={status}
        successMessage="Money request added successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default MoneyRequestPopup;
